import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  label: string
  placeholder: string
  /** All selectable values (e.g. merkeOptions / modellOptions). */
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  disabledHint?: string // shown next to the label while disabled
  maxSuggestions?: number
}

/**
 * Compact multi-select: search-to-filter input + a dropdown of matches +
 * selected values shown as removable pills. Replaces a scrollable wall of
 * chip buttons (issue found via Playwright testing of #274 Fase 3, learning
 * from the single-select pattern in Leads' Biler tab while keeping
 * multi-select — Ståtid compares several merker as separate scatter series).
 */
export default function MultiSelectTypeahead({
  label, placeholder, options, selected, onChange, disabled = false, disabledHint,
  maxSuggestions = 12,
}: Props) {
  const { t } = useTranslation('staatid')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase()
    const available = options.filter(o => !selected.includes(o))
    const matches = q ? available.filter(o => o.toLowerCase().includes(q)) : available
    return matches.slice(0, maxSuggestions)
  }, [options, selected, query, maxSuggestions])

  function add(value: string) {
    onChange([...selected, value])
    setQuery('')
    // Close rather than leave the suggestion list open: with a long option
    // list (e.g. every model for a brand) it can overlay controls below it
    // (found via Playwright — it blocked the "Vis resultater" button).
    setOpen(false)
  }

  function remove(value: string) {
    onChange(selected.filter(v => v !== value))
  }

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {label}{disabled && disabledHint ? ` — ${disabledHint}` : ''}
      </p>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {selected.map(v => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2 py-1"
            >
              {v}
              <button
                type="button"
                aria-label={t('common.remove', { name: v })}
                onClick={() => remove(v)}
                className="text-primary-500 hover:text-primary-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className="w-full text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {open && !disabled && suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm">
            {suggestions.map(o => (
              <li key={o}>
                <button
                  type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => add(o)}
                  className="block w-full text-left text-sm px-2 py-1 hover:bg-gray-50 dark:hover:bg-dark-border"
                >
                  {o}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
